// HTTP chat protocol — mirrors PROTOCOL.md §1.

import type { RequestCostSummary } from './cost';
import type { FormRequest, FormSubmission } from './form';
import type { HandoffPayload } from './handoff';
import type {
  ChatSuccessMeta,
  CorpBillingOutcome,
  CrossSellSuggestion,
  EticketMeta,
  HoldPayload,
  InvoiceMeta,
  LiveHandoffSignal,
  PaymentMeta,
  PolicyViolationMeta,
  TicketMeta,
  TicketPendingSignal,
} from './meta';
import type { RequestLocale, SiteContext, UserHint } from './site';

export type ChatRole = 'user' | 'assistant' | 'system';

export interface ChatMessage {
  role: ChatRole;
  content: string;
}

/**
 * Channel identifier — see spec/PROTOCOL.md §1. FEATURE-64 introduces this
 * field; the panel hard-codes `'widget'` (only channel it speaks for). The
 * server-side data model is multi-channel from day one so adding the Zalo
 * OA / email adapters later is a write, not a migration.
 */
export type ChatChannel = 'widget' | 'zalo' | 'email';

export interface ChatRequest {
  sessionId: string;
  /** FEATURE-64 — channel originating this turn. Required on the wire. */
  channel: ChatChannel;
  /**
   * FEATURE-64 — the single new user turn. The BE persists the full
   * conversation server-side; the FE no longer mirrors history on the
   * wire. `messages` (the legacy field) is deprecated and accepted for
   * a 30-day window before removal.
   */
  userMessage: ChatMessage;
  /** @deprecated FEATURE-64 — kept only for the 30-day legacy-bundle window. */
  messages?: ChatMessage[];
  siteContext?: SiteContext;
  user?: UserHint;
  /**
   * FEATURE-66 — `'vi'` / `'en'` honored verbatim; `'auto'` triggers
   * BE-side language detection (FEATURE-42 / FEATURE-56). Omit (per
   * `JSON.stringify` dropping `undefined` keys) for the same auto-detect
   * behavior — observable BE response is identical.
   */
  locale?: RequestLocale;
  /**
   * FEATURE-68 — present when the user submitted (or dismissed) a
   * structured form card from the prior turn. When set, the BE bypasses
   * `RouterAgent` and dispatches directly to the originating specialist
   * via the `<!-- ba-form -->` manifest. See PROTOCOL.md §1.14.
   */
  formSubmission?: FormSubmission;
  /**
   * FEATURE-87 — the embedding host's origin, computed by the PANEL from the
   * browser-enforced `window.location.ancestorOrigins[0]` (Firefox fallback:
   * origin of `document.referrer`). Normalized `scheme://host[:port]`. The BE
   * validates it against the admin-managed allowlist and rejects unlisted
   * origins with `403 HOST_NOT_ALLOWED`. Host-page JS cannot forge
   * `ancestorOrigins`; the launcher-supplied `siteContext.pageUrl` is NOT used
   * for this check. See PROTOCOL.md §1.15.
   */
  hostOrigin?: string;
  /**
   * FEATURE-88 — tenant identifier ("client") for multi-tenant deployments.
   * Kebab-case, `^[a-z0-9-]{3,64}$`. Set by the host via
   * `BAAssistant.init({ clientCode })` (preferred) or via the
   * `<meta name="ba-assistant-client">` fallback for legacy embeds. The panel
   * also sets the `X-Client-Code` HTTP header (informational; body is
   * authoritative).
   *
   * Phase 3 transitional mode: optional on the wire. When missing, the BE
   * falls back to the seeded `worldtrans` tenant. Phase 5 will flip this to
   * required + reject missing values with `400 CLIENT_CODE_REQUIRED`.
   *
   * Error codes added in FEATURE-88:
   *   - `400 CLIENT_CODE_REQUIRED` — invalid format / missing in strict mode
   *   - `403 CLIENT_INACTIVE` — unknown or disabled tenant
   *   - `403 CLIENT_ORIGIN_MISMATCH` — origin doesn't match `clients.url`
   * See PROTOCOL.md §1.16.
   */
  clientCode?: string;
}

/**
 * FEATURE-88 — kebab-case client-code format. Re-exported so the launcher
 * (boundary validation) and the panel (sanity check on a stored value) share
 * the exact pattern used by the BE DTO.
 */
export const CLIENT_CODE_PATTERN = /^[a-z0-9-]{3,64}$/;

/**
 * FEATURE-98 — request body for the deterministic, LLM-free ticket re-check
 * endpoint `POST /api/v1/booking/ticket-status` (PROTOCOL.md §1.19). Same
 * `clientCode` / `X-Client-Code` + `user.authToken` passthrough as §1; the
 * widget calls it `recheckAfterMs` after a turn carrying `meta.ticketPending`.
 */
export interface TicketStatusRequest {
  sessionId: string;
  channel: ChatChannel;
  clientCode: string;
  pnrNumber: string;
  /**
   * Host `user` hint — carries the optional `authToken` the BE validates at
   * the use-site and impersonates with (same passthrough as §1). REQUIRED in
   * practice for an agency / logged-in PNR: without it the BE's
   * `retrieveBooking` runs unauthenticated and APIhub 403/404s the booking,
   * surfacing the recheck error on every issuance. NOT auth — see §Security.
   */
  user?: UserHint;
  /**
   * Embedding host origin — `ClientGuard` compares it against `clients.url`
   * (FEATURE-88). REQUIRED in practice: the panel iframe's browser `Origin`
   * is the widget origin, not the host site's, so the guard relies on this
   * body field. Omitting it makes the guard see no origin and 403 the
   * re-check with `CLIENT_ORIGIN_MISMATCH` (UAT: `got=-`). Same field the
   * chat request sends.
   */
  hostOrigin?: string;
}

/**
 * FEATURE-98 — outcome of a ticket re-check (PROTOCOL.md §1.19):
 *   - `issued`   → carrier confirmed; `assistantMessage` + `ticket` present,
 *                  BE has persisted the message server-side.
 *   - `issuing`  → still HOLD (not TICKETED yet); message NOT persisted, the
 *                  widget may re-poll a small number of times before degrading.
 *   - `failed`   → terminal failure; honest message, no fabricated success.
 */
export type TicketStatusOutcome = 'issued' | 'issuing' | 'failed';

/** FEATURE-98 — success-envelope `data` for `POST /api/v1/booking/ticket-status`. */
export interface TicketStatusData {
  status: TicketStatusOutcome;
  /** role:'assistant'; tool-grounded copy + `<!-- ba-tickets -->` trailer. */
  assistantMessage: ChatMessage;
  /** §1.7; present on `'issued'`. */
  ticket?: TicketMeta;
  /** Present for an agency identity when `getAgencyCredit` confirmed. */
  creditRemainingVnd?: number;
  /**
   * Downloadable mặt vé face(s) ("tải mặt vé"). Present on `'issued'` when the
   * retrieved booking carries ticketImage(s); the widget renders the EticketCard
   * download link inline with the post-issuance confirmation. Additive within
   * protocol v1 — older clients ignore it.
   */
  eticket?: EticketMeta;
}

/** FEATURE-98 — full success/error envelope for the re-check endpoint. */
export type TicketStatusResponse =
  | { success: true; data: TicketStatusData }
  | ChatError;

/**
 * FEATURE-102 — outcome of the deterministic, LLM-free mask-ticket fetch
 * (`POST /api/v1/booking/eticket-mask`):
 *   - `ready`        → mask-ticket HTML rendered; `html` + `templateName` present.
 *   - `not-ticketed` → the PNR exists but isn't TICKETED yet (no face to render).
 *   - `not-found`    → the PNR couldn't be retrieved (4xx upstream).
 *   - `no-template`  → no enabled FLIGHT mask-ticket template is configured.
 *   - `error`        → mask-ticket preview failed (empty / threw upstream).
 */
export type EticketMaskStatus =
  | 'ready'
  | 'not-ticketed'
  | 'not-found'
  | 'no-template'
  | 'error';

/**
 * FEATURE-102 — success-envelope `data` for `POST /api/v1/booking/eticket-mask`.
 * Mirrors the BE locked contract (deterministic, no LLM). `html` +
 * `templateName` are present iff `status === 'ready'`; `message` is a VN ack
 * for the non-ready statuses so the widget never opens an empty overlay.
 */
export interface EticketMaskData {
  status: EticketMaskStatus;
  pnrNumber: string;
  /** Present iff `status === 'ready'` — the chosen mask-ticket HTML face. */
  html?: string;
  /** Present iff `status === 'ready'` — the picked template's display name. */
  templateName?: string;
  /** VN ack for `status !== 'ready'`. */
  message?: string;
}

/** FEATURE-102 — full success/error envelope for the mask-ticket endpoint. */
export type EticketMaskResponse =
  | { success: true; data: EticketMaskData }
  | ChatError;

/**
 * Set by the pre-router scope guard (FEATURE-15) when a turn is deflected
 * to `refusal_scope` or `refusal_unsafe`. Absent on normal turns. Consumed
 * by FEATURE-33's `RefusalPill`.
 */
export type RefusalReason = 'out_of_scope' | 'unsafe';

export interface ChatSuccess {
  success: true;
  data: {
    assistantMessage: ChatMessage;
    messages: ChatMessage[];
    routeKey: string;
  };
  /**
   * Aggregated `meta` envelope — sibling of `data` per
   * `SuccessEnvelope` in `-be`'s `response.dto.ts`. Additive within
   * protocol v1 (PROTOCOL.md §1.2 / §1.3 / §1.4 / §1.5 / §1.6 / §1.7
   * / §1.8 / §1.9 / §1.11 + §4.2 cost). Older deployments may omit
   * it entirely.
   *
   * Earlier FE builds (FEATURE-31 commit) nested this under `data.meta`;
   * that contradicted the BE wire shape and silently dropped every
   * Round-7 meta block in production. The fix here aligns the type and
   * the apiClient access path to the BE.
   */
  meta?: ChatSuccessMeta;
}

export interface ChatError {
  success: false;
  error: { code: string; message: string };
}

export type ChatResponse = ChatSuccess | ChatError;

// Error code constants — extend as PROTOCOL.md grows.
export const ChatErrorCode = {
  INVALID_REQUEST: 'INVALID_REQUEST',
  AUTH_INVALID: 'AUTH_INVALID',
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  RATE_LIMITED: 'RATE_LIMITED',
  LLM_PROVIDER_ERROR: 'LLM_PROVIDER_ERROR',
  LLM_PROVIDER_TIMEOUT: 'LLM_PROVIDER_TIMEOUT',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  /** FEATURE-68 — formSubmission present but manifest missing / mismatched / DTO structurally invalid. */
  INVALID_FORM_SUBMISSION: 'INVALID_FORM_SUBMISSION',
  /** FEATURE-87 — `hostOrigin` absent or not on the admin-managed allowlist. */
  HOST_NOT_ALLOWED: 'HOST_NOT_ALLOWED',
  /** FEATURE-88 — `clientCode` missing or malformed (Phase 5 strict mode). */
  CLIENT_CODE_REQUIRED: 'CLIENT_CODE_REQUIRED',
  /** FEATURE-88 — `clientCode` resolves to an unknown / disabled tenant. */
  CLIENT_INACTIVE: 'CLIENT_INACTIVE',
  /** FEATURE-88 — request origin doesn't match the tenant's registered `url`. */
  CLIENT_ORIGIN_MISMATCH: 'CLIENT_ORIGIN_MISMATCH',
} as const;

export type ChatErrorCodeValue = (typeof ChatErrorCode)[keyof typeof ChatErrorCode];

// Streaming chat — mirrors PROTOCOL.md §1.2.
//
// Server sends exactly one `meta` first, then one `chunk` per token-batch,
// then exactly one terminal event (`done` or `error`), then closes.
//
// `done.payload.cost` is additive within protocol v1 (FEATURE-14); older
// clients that don't read it must still work. `hold` / `payment` /
// `ticket` / `crossSell` / `corpBilling` / `policyViolation` / `invoice` /
// `handoff` / `refusalReason` are likewise additive (FEATURE-17 / 19 / 21
// / 26 / 20 / 24 / 22 / 13 / 15) and rendered by FEATURE-31 / 32 / 33.
export type StreamEvent =
  | { type: 'meta'; payload: { routeKey: string; provider: string; model: string } }
  | { type: 'chunk'; payload: { delta: string } }
  | {
      type: 'done';
      payload: {
        assistantMessage: ChatMessage;
        cost?: RequestCostSummary;
        hold?: HoldPayload;
        payment?: PaymentMeta;
        ticket?: TicketMeta;
        crossSell?: CrossSellSuggestion[];
        corpBilling?: CorpBillingOutcome;
        policyViolation?: PolicyViolationMeta;
        invoice?: InvoiceMeta;
        handoff?: HandoffPayload;
        refusalReason?: RefusalReason;
        /**
         * FEATURE-64 — set by the BE when persistence (RAG chat history)
         * failed for this turn. The reply still ships; the FE renders a
         * subtle one-line banner.
         */
        persistenceWarning?: boolean;
        /**
         * FEATURE-68 — structured form-card request. When present, the
         * panel renders `FormCard` above any other meta.* cards on the
         * same turn. See PROTOCOL.md §1.14.
         */
        form?: FormRequest;
        /**
         * FEATURE-93/94 — present once the session is human-handled. Tells
         * the widget to enter seamless human mode: poll for operator replies
         * and render them as ordinary assistant bubbles. Lives directly on
         * `done.payload` (flat-meta shape). See PROTOCOL.md §1.18.
         */
        liveHandoff?: LiveHandoffSignal;
        /**
         * FEATURE-98 — present when ticket issuance was accepted but not yet
         * confirmed. Tells the widget to re-check ticket status (after
         * `recheckAfterMs`) via `POST /api/v1/booking/ticket-status`. Lives
         * directly on `done.payload` (flat-meta shape). See PROTOCOL.md §1.19.
         */
        ticketPending?: TicketPendingSignal;
        /**
         * E-ticket face(s) for download ("tải mặt vé"). Present when the
         * `getEticket` tool fetched a downloadable ticket image. Lives
         * directly on `done.payload` (flat-meta shape).
         */
        eticket?: EticketMeta;
      };
    }
  | { type: 'error'; payload: { code: string; message: string } };

export type StreamEventType = StreamEvent['type'];
